import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import type { Label, LabelScope } from '@@/packages/shared/types/models/label';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { SUPPLIER_INFO_QUERY } from '@/modules/org-info/api/queryKeys';
import { SUPPLIERS_QUERY } from '@/modules/connection-view-suppliers/api/queryKeys';
import { refreshSupplierScopeEventually } from '@/modules/profile/api/complianceConsistency';
import { SUPPLIER_LABELS_QUERY } from './queryKeys';
import { parseLabel, type RawLabel } from './parseLabel';

async function fetchSupplierLabels(supplierID: string): Promise<Label[]> {
  const res = await request<RawLabel[]>('GET', `/supplier/${supplierID}/labels`);
  return res.data.map(parseLabel);
}

export function useSupplierLabels(
  supplierID: MaybeRefOrGetter<string>,
  scope?: MaybeRefOrGetter<LabelScope | undefined>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_LABELS_QUERY, toRef(supplierID)] as const,
      queryFn: ({ queryKey }) => fetchSupplierLabels(queryKey[1]),
    }),
  );

  const labels = computed(() => {
    const all = query.data.value || [];
    const wanted = toValue(scope);
    return wanted ? all.filter((label) => label.scope === wanted) : all;
  });
  const labelsByID = computed(() =>
    Object.fromEntries(labels.value.map((label) => [label.id, label])),
  );

  return { ...query, labels, labelsByID };
}

type UpdateSupplierLabelsVariables = {
  supplierID: string;
  scope?: LabelScope;
  /**
   * The labels to apply within `scope`, with applied labels of the other scope kept.
   * Without a `scope` this is the whole applied set and is sent as given.
   */
  labelIDs: string[];
};

export function useUpdateSupplierLabels(
  options?: MutationOptions<Record<string, never>, UpdateSupplierLabelsVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(variables) {
        const { scope, supplierID } = variables;
        let { labelIDs } = variables;
        if (scope) {
          // The PUT replaces the org's whole applied set, so labels of the other scope must be resent.
          const applied = await queryClient.fetchQuery({
            queryKey: [SUPPLIER_LABELS_QUERY, supplierID] as const,
            queryFn: () => fetchSupplierLabels(supplierID),
            staleTime: 0,
          });
          const otherScopeIDs = applied
            .filter((label) => label.scope !== scope)
            .map((label) => label.id);
          labelIDs = [...new Set([...labelIDs, ...otherScopeIDs])];
        }
        const res = await request<Record<string, never>>('PUT', `/supplier/${supplierID}/labels`, {
          labelIDs,
        });
        return res.data;
      },
      async onSuccess(_data, variables) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_LABELS_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_INFO_QUERY, variables.supplierID],
          }),
        ]);

        refreshSupplierScopeEventually(queryClient, variables.supplierID);
      },
    }),
  );
}
